// components/projects/AssignEngineerModal.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Paper,
  Chip,
  Avatar,
  Alert,
  Divider,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";

const AssignEngineerModal = ({
  open,
  onClose,
  project,
  onAssign,
  showSnackbar,
}) => {
  const { token } = useAuth();
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  // Fetch engineers from API
  const fetchEngineers = async (search = "") => {
    try {
      if (search) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }

      setError(null);

      const params = {
        role: "ENGINEER",
        status: "APPROVED",
        limit: 50,
      };

      if (search) {
        params.search = search;
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: params,
      };

      const response = await axios.get(`${url}/api/users/fetch`, config);

      if (response.data?.users) {
        setEngineers(response.data.users);
      } else {
        setEngineers([]);
      }
    } catch (err) {
      console.error("Error fetching engineers:", err);
      setError("Failed to load engineers. Please try again.");
      setEngineers([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchEngineers();
      setSelectedEngineer(null);
      setSearchTerm("");
      setSearchInput("");
      setError(null);
    }
  }, [open]);

  const handleSearchInputChange = (value) => {
    setSearchInput(value);
  };

  const handleEngineerSelect = (event, newValue) => {
    setSelectedEngineer(newValue);
  };

  // Calculate engineer stats
  const getEngineerStats = (engineer) => {
    const stats = engineer.projectsRecords || {};
    return {
      assigned: stats.projectAssigned || 0,
      completed: stats.projectCompleted || 0,
    };
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Get color for avatar
  const getAvatarColor = (name) => {
    const colors = [
      COLORS.SECONDARY,
    //   "#8CD47E",
    //   "#4CAF50",
    //   "#2196F3",
    //   "#FF9800",
      "#9C27B0",
    ];
    if (!name) return COLORS.SECONDARY;
    const index = name.length % colors.length;
    return colors[index];
  };

  const handleAssign = async () => {
    if (!selectedEngineer) {
      showSnackbar("Please select an engineer", "warning");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      const response = await axios.post(
        `${url}/api/inhouse-projects/assign-engineer/${project._id}`,
        {
          engineerId: selectedEngineer._id,
        },
        config
      );

      if (response.status === 200) {
        showSnackbar(
          `Engineer ${selectedEngineer.name} assigned successfully!`,
          "success"
        );
        
        if (onAssign) {
          onAssign(response.data.project);
        }
        
        onClose();
      }
    } catch (err) {
      console.error("Error assigning engineer:", err);
      const errorMsg = err.response?.data?.message || "Failed to assign engineer";
      setError(errorMsg);
      showSnackbar(errorMsg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1400,
        }}
      />

      {/* Modal */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90vw", sm: 600, md: 700 },
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
          zIndex: 1401,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2, sm: 3 },
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            bgcolor: COLORS.SECONDARY + "08", // Light background with opacity
          }}
        >
          <Box>
            <Typography
              sx={{
                ...TYPOGRAPHY.H2,
                color: COLORS.SECONDARY,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            >
              Assign Engineer
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_LARGE,
                color: COLORS.TEXT,
                mt: 0.5,
              }}
            >
              Project: {project.projectName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Current Assignment (if any) */}
        {project.assignedEngineer && (
          <Alert
            severity="info"
            sx={{
              m: { xs: 2, sm: 3 },
              mt: 2,
              borderRadius: 1,
              border: `1px solid ${COLORS.SECONDARY}20`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon name="info" color={COLORS.SECONDARY} />
              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL }}>
                Currently assigned to:{" "}
                <strong style={{ color: COLORS.SECONDARY }}>
                  {project.assignedEngineerName}
                </strong>
                {project.engineerAssignmentDate && (
                  <>
                    {" "}
                    since{" "}
                    {new Date(project.engineerAssignmentDate).toLocaleDateString()}
                  </>
                )}
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Search and Selection */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2 }}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              color: COLORS.SECONDARY,
              mb: 2,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Select Engineer
          </Typography>
          
          <Autocomplete
            value={selectedEngineer}
            onChange={handleEngineerSelect}
            inputValue={searchInput}
            onInputChange={(event, newInputValue) => {
              handleSearchInputChange(newInputValue);
            }}
            options={engineers}
            getOptionLabel={(option) => option?.name || ""}
            loading={searchLoading}
            disabled={loading || submitting}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type to search engineers..."
                variant="outlined"
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: COLORS.SECONDARY,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: COLORS.SECONDARY,
                      borderWidth: 2,
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Icon
                      name="search"
                      color={COLORS.LIGHT_SECONDARY}
                      sx={{ mr: 1, ml: 0.5 }}
                    />
                  ),
                  endAdornment: (
                    <>
                      {searchLoading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option._id}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 2,
                  width: "100%",
                  py: 1,
                }}>
                  <Avatar
                    sx={{
                      bgcolor: getAvatarColor(option.name),
                      width: 40,
                      height: 40,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    {getInitials(option.name)}
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: COLORS.TEXT,
                        mb: 0.25,
                      }}
                    >
                      {option.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: COLORS.MUTED_TEXT,
                      }}
                    >
                      {option.email}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Chip
                      label={`${getEngineerStats(option).assigned}`}
                      size="small"
                      sx={{
                        bgcolor: "#E3F2FD",
                        color: COLORS.SECONDARY,
                        fontSize: "0.75rem",
                        height: 22,
                        minWidth: 40,
                      }}
                      title="Projects Assigned"
                    />
                    <Chip
                      label={`${getEngineerStats(option).completed}`}
                      size="small"
                      sx={{
                        bgcolor: "#E8F5E9",
                        color: "#4CAF50",
                        fontSize: "0.75rem",
                        height: 22,
                        minWidth: 40,
                      }}
                      title="Projects Completed"
                    />
                  </Box>
                </Box>
              </Box>
            )}
            noOptionsText={
              searchInput ? "No engineers found" : "No engineers available"
            }
            isOptionEqualToValue={(option, value) => 
              option._id === value?._id
            }
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.name?.toLowerCase().includes(inputValue) ||
                option.email?.toLowerCase().includes(inputValue) ||
                option.phone?.includes(inputValue)
              );
            }}
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 9999,
                },
              },
            }}
          />
          
          {selectedEngineer && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: `2px solid ${COLORS.SECONDARY}`,
                bgcolor: COLORS.SECONDARY + "08",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* <Avatar
                  sx={{
                    bgcolor: getAvatarColor(selectedEngineer.name),
                    width: 50,
                    height: 50,
                    fontWeight: 600,
                  }}
                >
                  {getInitials(selectedEngineer.name)}
                </Avatar> */}
                
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.SECONDARY,
                      mb: 0.5,
                    }}
                  >
                    {selectedEngineer.name}
                  </Typography>
                  
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_SMALL,
                        color: COLORS.MUTED_TEXT,
                      }}
                    >
                      {selectedEngineer.email}
                    </Typography>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_SMALL,
                        color: COLORS.MUTED_TEXT,
                      }}
                    >
                      {selectedEngineer.phone}
                    </Typography>
                  </Box>
                  
                  {/* <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <Chip
                      icon={<Icon name="briefcase" size={14} />}
                      label={`${getEngineerStats(selectedEngineer).assigned} Assigned`}
                      size="small"
                      sx={{
                        bgcolor: "#E3F2FD",
                        color: COLORS.SECONDARY,
                        ...TYPOGRAPHY.BODY_SMALL,
                        height: 24,
                      }}
                    />
                    <Chip
                      icon={<Icon name="checkCircle" size={14} />}
                      label={`${getEngineerStats(selectedEngineer).completed} Completed`}
                      size="small"
                      sx={{
                        bgcolor: "#E8F5E9",
                        color: "#4CAF50",
                        ...TYPOGRAPHY.BODY_SMALL,
                        height: 24,
                      }}
                    />
                  </Box> */}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Error Message */}
        {error && (
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
            <Alert
              severity="error"
              sx={{ borderRadius: 1 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: { xs: 2, sm: 3 },
            borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            gap: 2,
            mt: "auto",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<Icon name="close2" />}
            disabled={submitting}
            sx={{
              borderColor: COLORS.LIGHT_SECONDARY,
              color: COLORS.SECONDARY,
              "&:hover": {
                borderColor: COLORS.SECONDARY,
                color: COLORS.SECONDARY,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedEngineer || submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon name="check" />
              )
            }
            sx={{
              bgcolor: COLORS.SECONDARY,
              "&:hover": {
                bgcolor: COLORS.SECONDARY,
                opacity: 0.9,
              },
              "&.Mui-disabled": {
                bgcolor: COLORS.LIGHT_SECONDARY,
                color: "#fff",
              },
            }}
          >
            {submitting ? "Assigning..." : "Assign Engineer"}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AssignEngineerModal;